import React from 'react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import { Happyemoji } from 'iconsax-react'

import AttachmentIcon from './Icons/AttachmentIcon'
import MicrophoneIcon from './Icons/MicrophoneIcon'
import ThunderIcon from './Icons/ThunderIcon'
import SendIcon from './Icons/SendIcon'

import EmojiSelect from '../../../ChatWindow/Controls/TextArea/EmojiSelect'
import UploadFile from '../../../UploadFile'
import EnterMenu from './EnterMenu'

import {
  insertEmoji,
  selectChatBox,
  updateValue
} from '../../../../../slices/chatBoxSlice'
import { useCanSend } from '../../../../../providers/CanSendProvider'
import { useChatEditState } from '../../../../../providers/ChatEditStateProvider'
import { useEnterMode } from '../../../../../providers/EnterModeProvider'
import useStyles from './styles'

const InputControls = ({ inputDisabled, sendMessage }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { values } = useAppSelector(selectChatBox)

  const { canSend } = useCanSend()
  const {
    state: editState,
    submit: submitEdit,
    end: endEdit
  } = useChatEditState()
  const { resetTemp } = useEnterMode()

  const isPrivate = React.useMemo(() => values.isPrivate, [values.isPrivate])
  const fillColor = React.useMemo(
    () => (isPrivate ? theme.palette.primary.main : '#8F8F8F'),
    [isPrivate, theme]
  )

  const disabled = !canSend || inputDisabled
  const classes = useStyles({ canSend: !disabled, isPrivate })

  const noClick = React.useCallback(e => {
    e.stopPropagation()
  }, [])

  const handleSend = React.useCallback(
    e => {
      e.stopPropagation()
      if (editState.editing) {
        submitEdit()
        resetTemp()
      } else {
        sendMessage(true)
      }
    },
    [editState.editing, submitEdit, resetTemp, sendMessage]
  )

  const handleEmojiSelect = React.useCallback(
    emoji => {
      const val = emoji.native

      dispatch(insertEmoji(val))
    },
    [dispatch]
  )

  const handleSetFile = React.useCallback(
    f => {
      endEdit()
      if (f) {
        dispatch(
          updateValue('file', old => [...(old ?? []), f].filter(Boolean))
        )
      }
    },
    [endEdit, dispatch]
  )

  return (
    <div className={classes.root}>
      <div className={classes.iconButtonBlock}>
        <EmojiSelect handleEmojiSelect={handleEmojiSelect} disabled={disabled}>
          {props => (
            <Happyemoji
              size='24'
              color={fillColor}
              className={classes.iconButton}
              {...props}
            />
          )}
        </EmojiSelect>

        <UploadFile
          onSetFile={handleSetFile}
          fileLength={Boolean(values.file?.length === 5)}
          disabled={disabled}
          enablePastingFiles
        >
          <AttachmentIcon fill={fillColor} className={classes.iconButton} />
        </UploadFile>

        <MicrophoneIcon
          fill={fillColor}
          className={classes.iconButton}
          onClick={noClick}
        />
      </div>

      <div className={classes.sendBlock}>
        {!isPrivate && (
          <div className={classes.cannedButton} onClick={noClick}>
            <ThunderIcon />
          </div>
        )}

        <div className={classes.sendButton} onClick={handleSend}>
          {isPrivate ? 'Add Note' : 'Send'} <SendIcon className='icon' />
        </div>

        <EnterMenu />
      </div>
    </div>
  )
}

export default InputControls
